// Web Push helper - registers the service worker and manages the racer's
// push subscription for race reminders. Degrades gracefully where unsupported
// (notably iOS Safari unless the app is added to the Home Screen).
import api from "@/lib/api";

export function isPushSupported() {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// Rough detection of an iOS browser that isn't running as an installed PWA.
export function needsHomeScreenInstall() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
  return isIOS && !standalone && !isPushSupported();
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
}

// 'unsupported' | 'denied' | 'enabled' | 'default'
export async function getPushStatus() {
  if (!isPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    return sub ? "enabled" : "default";
  } catch (e) {
    return "default";
  }
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Push isn't supported on this device/browser.");
  const reg = await getRegistration();
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications permission was not granted.");
  const { data } = await api.get("/push/vapid-public-key");
  if (!data.configured || !data.key) throw new Error("Push is not configured on the server.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return "enabled";
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
    }
  } catch (e) { /* ignore */ }
  return "disabled";
}

// Best-effort: if the racer already granted permission, make sure a fresh
// subscription is on file (called silently after registering for a contest).
export async function refreshSubscriptionIfGranted() {
  try {
    if (!isPushSupported() || Notification.permission !== "granted") return;
    await enablePush();
  } catch (e) { /* silent */ }
}
